import type { AudioPlayerCardProps } from "../../types/audio-player-card";

import { cn } from "../../utils/classnames";
import { AudioPlayer } from "../AudioPlayer";
import { AudioPlayerCardCoverArt } from "./AudioPlayerCardCoverArt";
import { AudioPlayerCardMeta } from "./AudioPlayerCardMeta";

/**
 * Layered to read as a single soft lift rather than one hard drop shadow; the
 * near-transparent outer layers are what keep the edge from banding.
 */
const CARD_CLASS_NAME =
  "rounded-2xl bg-white p-4 text-new-beginnings-base shadow-[0px_5px_11px_0px_rgba(44,79,37,0.1),0px_20px_20px_0px_rgba(44,79,37,0.09),0px_45px_27px_0px_rgba(44,79,37,0.05),0px_81px_32px_0px_rgba(44,79,37,0.01)] lg:rounded-[32px] lg:p-6";

/**
 * Presentational episode player card: cover art, episode meta, and the inline
 * transport controls.
 *
 * The controls share a row with the meta on wide viewports and drop below both
 * columns on narrow ones. That is a grid placement rather than two rendered
 * players, so the transport keeps one set of focusable controls at every width.
 */
function AudioPlayerCard({
  audioPlayer,
  className,
  coverArt,
  description,
  episodeNumber,
  primaryActionLink,
  publishedLabel,
  title,
  titleAs,
}: AudioPlayerCardProps) {
  const hasCoverArt = Boolean(coverArt);

  return (
    <div className={cn(CARD_CLASS_NAME, className)}>
      <div
        className={cn(
          "grid gap-4 lg:gap-x-5",
          hasCoverArt ? "grid-cols-[auto_minmax(0,1fr)]" : "grid-cols-1",
        )}
      >
        {coverArt ? (
          <AudioPlayerCardCoverArt
            className="lg:row-span-2"
            coverArt={coverArt}
          />
        ) : null}

        <AudioPlayerCardMeta
          description={description}
          episodeNumber={episodeNumber}
          primaryActionLink={primaryActionLink}
          publishedLabel={publishedLabel}
          title={title}
          titleAs={titleAs}
        />

        <AudioPlayer
          {...audioPlayer}
          className={cn(
            "min-w-0 lg:self-end",
            hasCoverArt ? "col-span-2 lg:col-span-1 lg:col-start-2" : "",
          )}
        />
      </div>
    </div>
  );
}

export { AudioPlayerCard };
